import { fetchWithAuth } from "./fetch-with-auth";
import { API_URL } from "constants/index";

export type ZipJobStatus = "pending" | "processing" | "completed" | "failed";

export interface ZipJob {
  id: number;
  status: ZipJobStatus;
  totalFiles: number;
  includedCount?: number;
  skippedCount?: number;
  fileName?: string;
  error?: string;
  expiresAt?: string;
}

/**
 * Starts a BACKGROUND ZIP-download job for the given file IDs. Returns the job
 * id immediately — the user is notified in-app (and via the download page) when
 * the archive is ready, so they can navigate away meanwhile.
 */
export async function startZipDownloadJob(
  fileIds: number[],
  session: any,
  userId: string,
): Promise<ZipJob> {
  if (!session) throw new Error("Geen actieve sessie — log opnieuw in");
  if (!Array.isArray(fileIds) || fileIds.length === 0) {
    throw new Error("Geen bestanden geselecteerd");
  }

  const response = await fetchWithAuth(
    `${API_URL}/file/download-zip/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-user-id": userId },
      body: JSON.stringify({ fileIds }),
    },
    session,
  );

  if (!response.ok) {
    let message = `Kon download niet starten (${response.status})`;
    try {
      const data = await response.json();
      if (data?.message) {
        message = Array.isArray(data.message) ? data.message.join(", ") : data.message;
      }
    } catch {
      // keep default message
    }
    throw new Error(message);
  }

  return response.json();
}

/** Fetches the current status of a background ZIP-download job. */
export async function getZipJobStatus(
  jobId: number,
  session: any,
): Promise<ZipJob> {
  const response = await fetchWithAuth(
    `${API_URL}/file/download-zip/job/${jobId}/status`,
    { method: "GET" },
    session,
  );
  if (!response.ok) {
    throw new Error(`Kon downloadstatus niet ophalen (${response.status})`);
  }
  return response.json();
}

/**
 * Downloads the generated ZIP for a completed job and triggers a browser save.
 */
export async function downloadZipJobFile(
  jobId: number,
  session: any,
  userId: string,
): Promise<void> {
  if (!session) throw new Error("Geen actieve sessie — log opnieuw in");

  const response = await fetchWithAuth(
    `${API_URL}/file/download-zip/job/${jobId}`,
    { method: "GET", headers: { "x-user-id": userId } },
    session,
  );

  if (!response.ok) {
    let message = `Download mislukt (${response.status})`;
    try {
      const data = await response.json();
      if (data?.message) {
        message = Array.isArray(data.message) ? data.message.join(", ") : data.message;
      }
    } catch {
      // keep default message
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || `yallyhub-bestanden-${jobId}.zip`;

  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
